import type { SupermarketSection } from '@cadencia/contracts';

/**
 * Catalogo de alimentos. Vive EN CODIGO y se referencia por `slug`, igual que
 * el de ejercicios (ADR-024, extendido en ADR-036): es un dato estatico, se
 * versiona con la app, funciona offline desde el primer arranque y no gasta ni
 * una fila de sincronizacion. Los alimentos propios del usuario si son una
 * entidad sincronizada aparte.
 *
 * TODOS los valores son **por 100 g o 100 ml en crudo**, salvo los marcados
 * con `isCookedReference`, que llevan su `referenceNote` visible en la
 * interfaz (docs/NUTRITION_ENGINE.md §5.6).
 *
 * `carbs100` son carbohidratos **disponibles**, sin contar la fibra, como se
 * declara en el etiquetado europeo. Las kcal no se transcriben a mano: salen
 * de `atwaterKcal` (4 / 4 / 9, **fibra a 0**), porque un numero derivado no
 * puede contradecir a los macros de los que sale. El porque de ese cero esta
 * escrito entero encima de `atwaterKcal`, y es una convencion de contabilidad,
 * no una afirmacion fisiologica (ADR-040).
 *
 * Son **estimaciones de tablas de composicion**, no analisis del lote concreto
 * que el usuario compra. El margen tipico es de un 5-10 %.
 */

export const FOOD_CATALOG_VERSION = 1;

/**
 * La seccion vive en `@cadencia/contracts` desde la Fase 5: se persiste en
 * `shopping_list_items.section` y forma parte del CHECK de la tabla, y los
 * enums persistidos son contrato, no detalle del motor.
 */
export type { SupermarketSection };

/**
 * Fuente proteica principal. Se usa para la rotacion obligatoria del §5.4
 * (pescado y legumbre >= 2 dias por semana, no mas de 3 dias seguidos con la
 * misma fuente). `none` es lo que no aporta proteina relevante.
 */
export type ProteinSource =
  | 'fish'
  | 'shellfish'
  | 'poultry'
  | 'red_meat'
  | 'pork'
  | 'egg'
  | 'dairy'
  | 'legume'
  | 'none';

export interface Food {
  slug: string;
  nameEs: string;
  unitBase: 'g' | 'ml';
  /** Derivado por Atwater a partir de los macros; nunca se escribe a mano. */
  kcal100: number;
  protein100: number;
  /** Carbohidratos disponibles, sin fibra. */
  carbs100: number;
  fat100: number;
  fiber100: number;
  section: SupermarketSection;
  proteinSource: ProteinSource;
  /** `true` si los valores se refieren al producto ya elaborado. */
  isCookedReference: boolean;
  referenceNote: string | null;
  /** Peso de «1 unidad» (un huevo, una manzana mediana). */
  unitWeightG: number | null;
  /** Formato comercial habitual, para la lista de compra (§12.1). */
  commercialPackG: number | null;
  /** Densidad energetica alta y valor nutricional bajo: penaliza en el §5.2. */
  isTreat: boolean;
}

/**
 * Atwater 4/4/9, con la **fibra a 0 kcal**.
 *
 * El etiquetado europeo le da 2 kcal/g y esa es la energia real, asi que esto
 * es una CONVENCION de contabilidad, no una afirmacion fisiologica. El motivo
 * es que el sistema entero tiene que hablar en la misma moneda:
 *
 * Los objetivos del §4.1 se construyen repartiendo las kcal del dia entre
 * proteina, hidratos y grasa con 4/4/9. Si la comida ademas aporta energia por
 * la fibra, esas kcal no caben en ningun macro del objetivo: un dia con 50 g de
 * fibra arrastra 100 kcal invisibles, y el generador solo puede pagarlas
 * recortando hidratos. Medido: en un dia de carga alta eso son 16 g de hidratos
 * de menos, suficiente para no cerrar el dia dentro de la tolerancia del §5.3
 * por mucho que se afine la receta. No era un fallo del generador; era que las
 * dos partes contaban distinto.
 *
 * Consecuencia asumida: los valores del catalogo quedan un 2-4 % por debajo de
 * los publicados en los alimentos con mucha fibra. El sesgo es CONSTANTE y la
 * calibracion adaptativa del §2.3 lo absorbe sin enterarse, porque compara la
 * ingesta medida con este mismo criterio contra el cambio de peso real.
 *
 * La fibra se sigue registrando en `fiber100` y se usa como objetivo propio.
 */
export function atwaterKcal(macros: {
  protein100: number;
  carbs100: number;
  fat100: number;
  fiber100: number;
}): number {
  return Math.round(4 * macros.protein100 + 4 * macros.carbs100 + 9 * macros.fat100);
}

interface FoodOpts {
  fiber?: number;
  unitBase?: 'g' | 'ml';
  proteinSource?: ProteinSource;
  cookedNote?: string;
  unitWeightG?: number;
  packG?: number;
  isTreat?: boolean;
}

const F = (
  slug: string,
  nameEs: string,
  section: SupermarketSection,
  protein: number,
  carbs: number,
  fat: number,
  opts: FoodOpts = {},
): Food => {
  const fiber = opts.fiber ?? 0;
  return {
    slug,
    nameEs,
    unitBase: opts.unitBase ?? 'g',
    kcal100: atwaterKcal({ protein100: protein, carbs100: carbs, fat100: fat, fiber100: fiber }),
    protein100: protein,
    carbs100: carbs,
    fat100: fat,
    fiber100: fiber,
    section,
    proteinSource: opts.proteinSource ?? 'none',
    isCookedReference: opts.cookedNote !== undefined,
    referenceNote: opts.cookedNote ?? null,
    unitWeightG: opts.unitWeightG ?? null,
    commercialPackG: opts.packG ?? null,
    isTreat: opts.isTreat ?? false,
  };
};

export const FOODS: readonly Food[] = [
  // ------------------------------------------------------------------ carnes
  F('pechuga-pollo', 'Pechuga de pollo', 'meat', 22.5, 0, 2.6, { proteinSource: 'poultry', packG: 500 }),
  F('contramuslo-pollo', 'Contramuslo de pollo sin piel', 'meat', 19, 0, 7.5, { proteinSource: 'poultry', packG: 500 }),
  F('muslo-pollo', 'Muslo de pollo con piel', 'meat', 17.5, 0, 11.5, { proteinSource: 'poultry', packG: 600 }),
  F('pechuga-pavo', 'Pechuga de pavo', 'meat', 23.5, 0, 1.6, { proteinSource: 'poultry', packG: 500 }),
  F('ternera-filete-magro', 'Filete de ternera magro', 'meat', 21.5, 0, 4.5, { proteinSource: 'red_meat', packG: 400 }),
  F('ternera-picada-5', 'Carne picada de ternera 5 % grasa', 'meat', 21, 0, 5, { proteinSource: 'red_meat', packG: 500 }),
  F('entrecot-ternera', 'Entrecot de ternera', 'meat', 20, 0, 14, { proteinSource: 'red_meat', packG: 400 }),
  F('solomillo-cerdo', 'Solomillo de cerdo', 'meat', 21.5, 0, 3.5, { proteinSource: 'pork', packG: 400 }),
  F('lomo-cerdo', 'Lomo de cerdo', 'meat', 21, 0, 6.5, { proteinSource: 'pork', packG: 500 }),
  F('secreto-iberico', 'Secreto ibérico', 'meat', 17, 0, 22, { proteinSource: 'pork', packG: 400 }),
  F('cinta-lomo-adobada', 'Cinta de lomo adobada', 'meat', 21, 1, 5, { proteinSource: 'pork', packG: 400 }),
  F('conejo', 'Conejo', 'meat', 21, 0, 5.5, { proteinSource: 'red_meat', packG: 800 }),
  F('cordero-pierna', 'Pierna de cordero', 'meat', 18.5, 0, 13, { proteinSource: 'red_meat', packG: 700 }),
  F('higado-ternera', 'Hígado de ternera', 'meat', 20.5, 3, 3.8, { proteinSource: 'red_meat', packG: 300 }),
  F('jamon-serrano', 'Jamón serrano', 'meat', 30, 0.5, 12, { proteinSource: 'pork', packG: 100 }),
  F('jamon-cocido-extra', 'Jamón cocido extra', 'meat', 19, 1, 3.5, { proteinSource: 'pork', packG: 200 }),
  F('pavo-fiambre', 'Fiambre de pavo', 'meat', 20, 1.5, 2, { proteinSource: 'poultry', packG: 200 }),
  F('bacon', 'Bacon', 'meat', 17, 0.5, 30, { proteinSource: 'pork', packG: 200, isTreat: true }),
  F('chorizo', 'Chorizo', 'meat', 24, 2, 32, { proteinSource: 'pork', packG: 200, isTreat: true }),
  F('salchichas-pollo', 'Salchichas de pollo', 'meat', 14, 2, 10, { proteinSource: 'poultry', packG: 250 }),
  F('hamburguesa-ternera', 'Hamburguesa de ternera', 'meat', 19.5, 1, 12, { proteinSource: 'red_meat', packG: 400 }),
  F('pollo-asado-lonchas', 'Pechuga de pollo en lonchas', 'meat', 20, 1.5, 1.8, { proteinSource: 'poultry', packG: 200 }),
  F('pavo-picado', 'Carne picada de pavo', 'meat', 20, 0, 3, { proteinSource: 'poultry', packG: 500 }),
  F('morcillo-ternera', 'Morcillo de ternera', 'meat', 20.5, 0, 4.5, { proteinSource: 'red_meat', packG: 600 }),
  F('lomo-embuchado', 'Lomo embuchado', 'meat', 34, 1, 9, { proteinSource: 'pork', packG: 100 }),

  // ------------------------------------------------------------- pescados
  F('merluza', 'Merluza', 'fish', 17, 0, 1.8, { proteinSource: 'fish', packG: 500 }),
  F('bacalao-fresco', 'Bacalao fresco', 'fish', 18, 0, 0.7, { proteinSource: 'fish', packG: 500 }),
  F('bacalao-desalado', 'Bacalao desalado', 'fish', 21, 0, 1, { proteinSource: 'fish', packG: 400 }),
  F('salmon-fresco', 'Salmón fresco', 'fish', 20, 0, 13, { proteinSource: 'fish', packG: 500 }),
  F('salmon-ahumado', 'Salmón ahumado', 'fish', 23, 0, 9, { proteinSource: 'fish', packG: 100 }),
  F('atun-fresco', 'Atún fresco', 'fish', 23, 0, 5, { proteinSource: 'fish', packG: 400 }),
  F('atun-lata-natural', 'Atún claro al natural', 'canned', 24, 0, 1, { proteinSource: 'fish', packG: 156 }),
  F('atun-lata-oliva', 'Atún en aceite de oliva escurrido', 'canned', 23, 0, 8, { proteinSource: 'fish', packG: 156 }),
  F('sardina-fresca', 'Sardina', 'fish', 19, 0, 9.5, { proteinSource: 'fish', packG: 500 }),
  F('sardinas-lata-oliva', 'Sardinas en aceite de oliva', 'canned', 22, 0, 12, { proteinSource: 'fish', packG: 120 }),
  F('caballa', 'Caballa', 'fish', 19.5, 0, 11, { proteinSource: 'fish', packG: 500 }),
  F('boqueron', 'Boquerón', 'fish', 18.5, 0, 5, { proteinSource: 'fish', packG: 400 }),
  F('anchoas-lata', 'Anchoas en aceite', 'canned', 26, 0, 10, { proteinSource: 'fish', packG: 50 }),
  F('lubina', 'Lubina', 'fish', 19, 0, 4, { proteinSource: 'fish', packG: 500 }),
  F('dorada', 'Dorada', 'fish', 19.5, 0, 4.5, { proteinSource: 'fish', packG: 500 }),
  F('rape', 'Rape', 'fish', 17.5, 0, 0.8, { proteinSource: 'fish', packG: 400 }),
  F('gallo', 'Gallo', 'fish', 16.5, 0, 1.5, { proteinSource: 'fish', packG: 400 }),
  F('trucha', 'Trucha', 'fish', 19.5, 0, 6, { proteinSource: 'fish', packG: 400 }),
  F('pez-espada', 'Pez espada', 'fish', 20, 0, 6, { proteinSource: 'fish', packG: 400 }),
  F('mejillones', 'Mejillones', 'fish', 18, 3, 2.5, { proteinSource: 'shellfish', packG: 1000 }),
  F('mejillones-escabeche', 'Mejillones en escabeche', 'canned', 17, 4, 8, { proteinSource: 'shellfish', packG: 110 }),
  F('gambas', 'Gambas peladas', 'fish', 19, 0, 1, { proteinSource: 'shellfish', packG: 300 }),
  F('langostinos', 'Langostinos', 'fish', 20, 0, 1.2, { proteinSource: 'shellfish', packG: 500 }),
  F('calamar', 'Calamar', 'fish', 16, 1, 1.4, { proteinSource: 'shellfish', packG: 500 }),
  F('sepia', 'Sepia', 'fish', 16.5, 0.8, 1.2, { proteinSource: 'shellfish', packG: 500 }),
  F('pulpo', 'Pulpo cocido', 'fish', 18, 1.5, 1.5, { proteinSource: 'shellfish', packG: 500, cookedNote: 'Valores del pulpo ya cocido' }),
  F('almejas', 'Almejas', 'fish', 12, 2.5, 1, { proteinSource: 'shellfish', packG: 500 }),
  F('surimi', 'Palitos de surimi', 'frozen', 9, 14, 1, { proteinSource: 'fish', packG: 200 }),
  F('jurel', 'Jurel', 'fish', 19, 0, 6, { proteinSource: 'fish', packG: 500 }),
  F('berberechos-lata', 'Berberechos al natural', 'canned', 12, 2, 0.5, { proteinSource: 'shellfish', packG: 110 }),
  F('langostino-cocido', 'Langostino cocido', 'fish', 20, 0, 1, { proteinSource: 'shellfish', packG: 400, cookedNote: 'Valores del langostino ya cocido' }),

  // --------------------------------------------------------------- huevos
  F('huevo', 'Huevo', 'eggs', 12.6, 0.7, 9.5, { proteinSource: 'egg', unitWeightG: 60, packG: 720 }),
  F('clara-huevo', 'Clara de huevo', 'eggs', 10.8, 0.7, 0.2, { proteinSource: 'egg', unitWeightG: 33, packG: 1000 }),
  F('huevo-codorniz', 'Huevo de codorniz', 'eggs', 13, 0.4, 11, { proteinSource: 'egg', unitWeightG: 10 }),
  F('huevo-liquido', 'Huevo líquido pasteurizado', 'eggs', 12.5, 0.7, 9.5, { unitBase: 'ml', proteinSource: 'egg', packG: 1000 }),

  // -------------------------------------------------------------- lacteos
  F('leche-entera', 'Leche entera', 'dairy', 3.2, 4.7, 3.6, { unitBase: 'ml', proteinSource: 'dairy', packG: 1000 }),
  F('leche-semidesnatada', 'Leche semidesnatada', 'dairy', 3.2, 4.8, 1.6, { unitBase: 'ml', proteinSource: 'dairy', packG: 1000 }),
  F('leche-desnatada', 'Leche desnatada', 'dairy', 3.3, 4.9, 0.3, { unitBase: 'ml', proteinSource: 'dairy', packG: 1000 }),
  F('yogur-natural', 'Yogur natural', 'dairy', 3.8, 4.5, 3.2, { proteinSource: 'dairy', unitWeightG: 125, packG: 500 }),
  F('yogur-griego', 'Yogur griego natural', 'dairy', 4, 4, 9, { proteinSource: 'dairy', unitWeightG: 125, packG: 500 }),
  F('yogur-proteico', 'Yogur proteico natural', 'dairy', 10, 4, 0.2, { proteinSource: 'dairy', unitWeightG: 150, packG: 600 }),
  F('kefir', 'Kéfir natural', 'dairy', 3.5, 4.2, 3.3, { unitBase: 'ml', proteinSource: 'dairy', packG: 500 }),
  F('queso-batido-0', 'Queso batido 0 %', 'dairy', 8, 4, 0.3, { proteinSource: 'dairy', packG: 500 }),
  F('requeson', 'Requesón', 'dairy', 11, 3.5, 4.5, { proteinSource: 'dairy', packG: 250 }),
  F('queso-fresco-burgos', 'Queso fresco de Burgos', 'dairy', 12, 3.5, 11, { proteinSource: 'dairy', packG: 250 }),
  F('mozzarella', 'Mozzarella', 'dairy', 19, 2, 17, { proteinSource: 'dairy', packG: 125 }),
  F('queso-manchego-curado', 'Queso manchego curado', 'dairy', 29, 1, 33, { proteinSource: 'dairy', packG: 250 }),
  F('queso-semicurado', 'Queso semicurado', 'dairy', 25, 1.5, 26, { proteinSource: 'dairy', packG: 250 }),
  F('parmesano', 'Parmesano rallado', 'dairy', 33, 3, 28, { proteinSource: 'dairy', packG: 100 }),
  F('queso-crema-light', 'Queso crema ligero', 'dairy', 8, 5, 11, { proteinSource: 'dairy', packG: 200 }),
  F('nata-cocinar', 'Nata para cocinar 18 %', 'dairy', 2.5, 3.5, 18, { unitBase: 'ml', packG: 200 }),
  F('mantequilla', 'Mantequilla', 'dairy', 0.7, 0.6, 82, { packG: 250, isTreat: true }),
  F('bebida-soja', 'Bebida de soja sin azúcar', 'dairy', 3.3, 0.5, 1.9, { unitBase: 'ml', proteinSource: 'legume', packG: 1000 }),
  F('bebida-avena', 'Bebida de avena', 'dairy', 0.8, 7, 1.5, { unitBase: 'ml', packG: 1000 }),
  F('proteina-whey', 'Proteína de suero en polvo', 'other', 76, 6, 6, { proteinSource: 'dairy', packG: 1000 }),
  F('yogur-natural-desnatado', 'Yogur natural desnatado', 'dairy', 4.3, 6, 0.2, { proteinSource: 'dairy', unitWeightG: 125, packG: 500 }),
  F('queso-cottage', 'Queso cottage', 'dairy', 12, 3, 4, { proteinSource: 'dairy', packG: 300 }),
  F('bebida-almendra', 'Bebida de almendra sin azúcar', 'dairy', 0.5, 0.2, 1.1, { unitBase: 'ml', packG: 1000 }),

  // ------------------------------------------------------------- cereales
  F('arroz-blanco', 'Arroz blanco largo', 'cereals', 7, 77, 0.8, { fiber: 1.3, packG: 1000 }),
  F('arroz-bomba', 'Arroz bomba', 'cereals', 7.5, 76, 0.9, { fiber: 1.4, packG: 1000 }),
  F('arroz-integral', 'Arroz integral', 'cereals', 8, 71, 2.5, { fiber: 3.5, packG: 1000 }),
  F('pasta-seca', 'Pasta seca', 'cereals', 12, 70, 1.5, { fiber: 3, packG: 500 }),
  F('pasta-integral', 'Pasta integral', 'cereals', 13, 62, 2.5, { fiber: 8, packG: 500 }),
  F('fideos-finos', 'Fideos finos', 'cereals', 12, 70, 1.5, { fiber: 3, packG: 500 }),
  F('cuscus', 'Cuscús', 'cereals', 12.5, 70, 1.5, { fiber: 5, packG: 500 }),
  F('bulgur', 'Bulgur', 'cereals', 12, 63, 1.5, { fiber: 12, packG: 500 }),
  F('quinoa', 'Quinoa', 'cereals', 14, 58, 6, { fiber: 7, packG: 500 }),
  F('avena-copos', 'Copos de avena', 'cereals', 13.5, 58, 7, { fiber: 10, packG: 1000 }),
  F('salvado-avena', 'Salvado de avena', 'cereals', 17, 47, 7, { fiber: 15, packG: 500 }),
  F('harina-avena', 'Harina de avena', 'cereals', 13.5, 58, 7, { fiber: 10, packG: 1000 }),
  F('espelta-grano', 'Espelta en grano', 'cereals', 14.5, 62, 2.5, { fiber: 9, packG: 500 }),
  F('pan-barra-blanco', 'Pan de barra', 'cereals', 8, 50, 1.5, { fiber: 2.5, packG: 250 }),
  F('pan-integral', 'Pan integral', 'cereals', 9, 41, 2.5, { fiber: 7, packG: 400 }),
  F('pan-molde-integral', 'Pan de molde integral', 'cereals', 9.5, 40, 3.5, { fiber: 6.5, unitWeightG: 30, packG: 460 }),
  F('pan-centeno', 'Pan de centeno', 'cereals', 8, 44, 1.5, { fiber: 8, packG: 500 }),
  F('pan-pita-integral', 'Pan de pita integral', 'cereals', 9, 45, 2, { fiber: 6, unitWeightG: 60, packG: 300 }),
  F('tostadas-integrales', 'Tostadas integrales', 'cereals', 11, 62, 5, { fiber: 8, unitWeightG: 10, packG: 300 }),
  F('tortilla-trigo', 'Tortilla de trigo', 'cereals', 8, 50, 7, { fiber: 3, unitWeightG: 50, packG: 400 }),
  F('harina-trigo', 'Harina de trigo', 'cereals', 10, 72, 1.2, { fiber: 3, packG: 1000 }),
  F('harina-integral', 'Harina integral de trigo', 'cereals', 12, 60, 2, { fiber: 10, packG: 1000 }),
  F('pan-rallado', 'Pan rallado', 'cereals', 11, 68, 2.5, { fiber: 4, packG: 500 }),
  F('maiz-dulce-lata', 'Maíz dulce en conserva', 'canned', 3, 16, 1.5, { fiber: 2.5, packG: 300 }),
  F('polenta', 'Sémola de maíz', 'cereals', 8, 74, 1.5, { fiber: 4, packG: 500 }),
  F('muesli-sin-azucar', 'Muesli sin azúcar añadido', 'cereals', 11, 58, 8, { fiber: 9, packG: 750 }),
  F('cereales-maiz', 'Copos de maíz', 'cereals', 7, 82, 0.9, { fiber: 3, packG: 500, isTreat: true }),
  F('masa-pizza-integral', 'Masa de pizza integral', 'cereals', 9, 46, 5, { fiber: 5, unitWeightG: 260 }),
  F('picos-integrales', 'Picos integrales', 'cereals', 10, 60, 12, { fiber: 6, packG: 250, isTreat: true }),
  F('galleta-avena', 'Galletas de avena', 'cereals', 8, 60, 15, { fiber: 6, unitWeightG: 15, packG: 300, isTreat: true }),
  F('arroz-basmati', 'Arroz basmati', 'cereals', 8, 77, 0.9, { fiber: 1.3, packG: 1000 }),
  F('trigo-sarraceno', 'Trigo sarraceno', 'cereals', 13, 62, 3.4, { fiber: 10, packG: 500 }),
  F('pan-hamburguesa-integral', 'Pan de hamburguesa integral', 'cereals', 9, 42, 5, { fiber: 5, unitWeightG: 70, packG: 280 }),
  F('crackers-integrales', 'Crackers integrales', 'cereals', 10, 60, 12, { fiber: 7, packG: 200 }),

  // ------------------------------------------------------------ legumbres
  F('garbanzos-secos', 'Garbanzos secos', 'legumes', 19, 45, 5, { fiber: 15, proteinSource: 'legume', packG: 500 }),
  F('garbanzos-cocidos', 'Garbanzos cocidos de bote', 'legumes', 7.5, 16, 2.5, { fiber: 6, proteinSource: 'legume', packG: 400, cookedNote: 'Valores del producto ya cocido' }),
  F('lentejas-secas', 'Lentejas secas', 'legumes', 24, 47, 1.5, { fiber: 12, proteinSource: 'legume', packG: 500 }),
  F('lentejas-cocidas', 'Lentejas cocidas de bote', 'legumes', 7.5, 13, 0.6, { fiber: 5, proteinSource: 'legume', packG: 400, cookedNote: 'Valores del producto ya cocido' }),
  F('alubias-blancas-secas', 'Alubias blancas secas', 'legumes', 21, 45, 1.5, { fiber: 16, proteinSource: 'legume', packG: 500 }),
  F('alubias-cocidas', 'Alubias blancas cocidas de bote', 'legumes', 7, 13, 0.5, { fiber: 6, proteinSource: 'legume', packG: 400, cookedNote: 'Valores del producto ya cocido' }),
  F('alubias-pintas-secas', 'Alubias pintas secas', 'legumes', 21.5, 44, 1.5, { fiber: 15, proteinSource: 'legume', packG: 500 }),
  F('judias-negras-secas', 'Judías negras secas', 'legumes', 21, 45, 1.4, { fiber: 15, proteinSource: 'legume', packG: 500 }),
  F('guisantes-secos', 'Guisantes secos', 'legumes', 22, 48, 1.5, { fiber: 14, proteinSource: 'legume', packG: 500 }),
  F('habas-secas', 'Habas secas', 'legumes', 25, 40, 1.5, { fiber: 16, proteinSource: 'legume', packG: 500 }),
  F('soja-texturizada', 'Soja texturizada', 'legumes', 50, 20, 1.5, { fiber: 18, proteinSource: 'legume', packG: 400 }),
  F('tofu-firme', 'Tofu firme', 'legumes', 15, 1.5, 8, { fiber: 1, proteinSource: 'legume', packG: 400 }),
  F('tempeh', 'Tempeh', 'legumes', 19, 7, 11, { fiber: 5, proteinSource: 'legume', packG: 200 }),
  F('hummus', 'Hummus', 'legumes', 7.5, 10, 15, { fiber: 6, proteinSource: 'legume', packG: 200, cookedNote: 'Valores del producto ya elaborado' }),
  F('lentejas-rojas-secas', 'Lentejas rojas secas', 'legumes', 24, 50, 1.5, { fiber: 9, proteinSource: 'legume', packG: 500 }),

  // ------------------------------------------------------------- verduras
  F('tomate', 'Tomate', 'produce', 0.9, 3, 0.2, { fiber: 1.2, unitWeightG: 150, packG: 1000 }),
  F('tomate-cherry', 'Tomate cherry', 'produce', 1, 3.2, 0.2, { fiber: 1.3, packG: 250 }),
  F('tomate-triturado', 'Tomate triturado', 'canned', 1.3, 4, 0.2, { fiber: 1.2, packG: 400 }),
  F('tomate-frito', 'Tomate frito', 'canned', 1.5, 7, 5, { fiber: 1.5, packG: 350 }),
  F('cebolla', 'Cebolla', 'produce', 1.2, 6, 0.2, { fiber: 1.7, unitWeightG: 150, packG: 1000 }),
  F('cebolleta', 'Cebolleta', 'produce', 1.5, 4, 0.2, { fiber: 2, unitWeightG: 50 }),
  F('ajo', 'Ajo', 'produce', 6, 24, 0.5, { fiber: 2, unitWeightG: 5, packG: 100 }),
  F('puerro', 'Puerro', 'produce', 1.5, 5, 0.3, { fiber: 2.5, unitWeightG: 150 }),
  F('pimiento-rojo', 'Pimiento rojo', 'produce', 1, 4.5, 0.3, { fiber: 1.7, unitWeightG: 180 }),
  F('pimiento-verde', 'Pimiento verde italiano', 'produce', 0.9, 3, 0.2, { fiber: 1.6, unitWeightG: 100 }),
  F('pimientos-piquillo', 'Pimientos del piquillo', 'canned', 1.2, 4, 0.3, { fiber: 2, packG: 200 }),
  F('calabacin', 'Calabacín', 'produce', 1.2, 2, 0.3, { fiber: 1.1, unitWeightG: 250 }),
  F('berenjena', 'Berenjena', 'produce', 1, 3, 0.2, { fiber: 3, unitWeightG: 250 }),
  F('zanahoria', 'Zanahoria', 'produce', 0.9, 7, 0.2, { fiber: 2.8, unitWeightG: 80, packG: 1000 }),
  F('patata', 'Patata', 'produce', 2, 16, 0.1, { fiber: 2.2, unitWeightG: 180, packG: 2000 }),
  F('boniato', 'Boniato', 'produce', 1.6, 18, 0.1, { fiber: 3, unitWeightG: 200, packG: 1000 }),
  F('brocoli', 'Brócoli', 'produce', 3, 2.5, 0.4, { fiber: 3, packG: 500 }),
  F('coliflor', 'Coliflor', 'produce', 2, 3, 0.3, { fiber: 2.5, packG: 800 }),
  F('judias-verdes', 'Judías verdes', 'produce', 1.8, 4, 0.2, { fiber: 3, packG: 500 }),
  F('espinaca', 'Espinacas frescas', 'produce', 2.9, 1.5, 0.4, { fiber: 2.2, packG: 250 }),
  F('acelga', 'Acelgas', 'produce', 1.8, 2, 0.2, { fiber: 1.6, packG: 500 }),
  F('lechuga', 'Lechuga', 'produce', 1.2, 1.5, 0.2, { fiber: 1.3, unitWeightG: 300 }),
  F('canonigos', 'Canónigos', 'produce', 2, 2, 0.3, { fiber: 1.5, packG: 100 }),
  F('rucula', 'Rúcula', 'produce', 2.6, 2, 0.7, { fiber: 1.6, packG: 100 }),
  F('esparragos-verdes', 'Espárragos verdes', 'produce', 2.2, 2, 0.2, { fiber: 2, packG: 250 }),
  F('esparragos-blancos-bote', 'Espárragos blancos en conserva', 'canned', 2, 1.5, 0.2, { fiber: 2, packG: 250 }),
  F('alcachofa', 'Alcachofa', 'produce', 3, 4, 0.2, { fiber: 5.4, unitWeightG: 100 }),
  F('champinon', 'Champiñones', 'produce', 3, 1, 0.3, { fiber: 1.5, packG: 400 }),
  F('setas-ostra', 'Setas de ostra', 'produce', 3.3, 2, 0.4, { fiber: 2.3, packG: 300 }),
  F('apio', 'Apio', 'produce', 0.7, 2, 0.2, { fiber: 1.6, unitWeightG: 60 }),
  F('pepino', 'Pepino', 'produce', 0.7, 2, 0.1, { fiber: 0.7, unitWeightG: 300 }),
  F('calabaza', 'Calabaza', 'produce', 1, 5, 0.1, { fiber: 1.5, packG: 800 }),
  F('col-lombarda', 'Lombarda', 'produce', 1.4, 4, 0.2, { fiber: 2.5, packG: 800 }),
  F('repollo', 'Repollo', 'produce', 1.3, 3, 0.2, { fiber: 2.5, packG: 800 }),
  F('coles-bruselas', 'Coles de Bruselas', 'produce', 3.4, 4, 0.3, { fiber: 3.8, packG: 500 }),
  F('remolacha-cocida', 'Remolacha cocida', 'produce', 1.6, 7, 0.2, { fiber: 2.5, packG: 500, cookedNote: 'Valores del producto ya cocido' }),
  F('aguacate', 'Aguacate', 'produce', 2, 1, 15, { fiber: 6.7, unitWeightG: 200 }),
  F('aceitunas-verdes', 'Aceitunas verdes', 'canned', 1, 1, 15, { fiber: 3, packG: 300 }),
  F('aceitunas-negras', 'Aceitunas negras', 'canned', 1.5, 1.5, 20, { fiber: 3.5, packG: 300 }),
  F('gazpacho-envasado', 'Gazpacho envasado', 'produce', 0.8, 3, 2.5, { fiber: 0.8, unitBase: 'ml', packG: 1000, cookedNote: 'Valores del producto ya elaborado' }),
  F('endibia', 'Endibia', 'produce', 1, 2, 0.2, { fiber: 2, unitWeightG: 100 }),
  F('nabo', 'Nabo', 'produce', 1, 4, 0.1, { fiber: 2, packG: 500 }),
  F('brotes-soja', 'Brotes de soja', 'produce', 3, 4, 0.2, { fiber: 1.8, packG: 300 }),
  F('alcachofas-conserva', 'Corazones de alcachofa en conserva', 'canned', 2.5, 3, 0.2, { fiber: 4, packG: 400 }),
  F('champinones-conserva', 'Champiñones laminados en conserva', 'canned', 2, 1, 0.3, { fiber: 2, packG: 300 }),

  // --------------------------------------------------------------- frutas
  F('manzana', 'Manzana', 'produce', 0.3, 12, 0.2, { fiber: 2.4, unitWeightG: 180, packG: 1000 }),
  F('pera', 'Pera', 'produce', 0.4, 12, 0.1, { fiber: 3, unitWeightG: 180 }),
  F('platano', 'Plátano', 'produce', 1.1, 20, 0.3, { fiber: 2.6, unitWeightG: 120, packG: 1000 }),
  F('naranja', 'Naranja', 'produce', 0.9, 9, 0.1, { fiber: 2.4, unitWeightG: 200, packG: 2000 }),
  F('mandarina', 'Mandarina', 'produce', 0.8, 11, 0.3, { fiber: 1.8, unitWeightG: 80, packG: 1000 }),
  F('kiwi', 'Kiwi', 'produce', 1.1, 11, 0.5, { fiber: 3, unitWeightG: 90 }),
  F('fresa', 'Fresas', 'produce', 0.7, 6, 0.3, { fiber: 2, packG: 500 }),
  F('sandia', 'Sandía', 'produce', 0.6, 8, 0.2, { fiber: 0.4, packG: 2000 }),
  F('melon', 'Melón', 'produce', 0.8, 8, 0.2, { fiber: 0.9, packG: 1500 }),
  F('uva', 'Uva', 'produce', 0.7, 16, 0.2, { fiber: 0.9, packG: 500 }),
  F('melocoton', 'Melocotón', 'produce', 0.9, 9, 0.3, { fiber: 1.5, unitWeightG: 150 }),
  F('nectarina', 'Nectarina', 'produce', 1.1, 9, 0.3, { fiber: 1.7, unitWeightG: 140 }),
  F('albaricoque', 'Albaricoque', 'produce', 1.4, 9, 0.4, { fiber: 2, unitWeightG: 50 }),
  F('ciruela', 'Ciruela', 'produce', 0.7, 10, 0.3, { fiber: 1.4, unitWeightG: 60 }),
  F('cereza', 'Cerezas', 'produce', 1, 13, 0.3, { fiber: 1.6, packG: 500 }),
  F('higo', 'Higo', 'produce', 0.8, 16, 0.3, { fiber: 2.9, unitWeightG: 50 }),
  F('granada', 'Granada', 'produce', 1.7, 15, 1.2, { fiber: 4, unitWeightG: 280 }),
  F('pina', 'Piña', 'produce', 0.5, 12, 0.1, { fiber: 1.4, packG: 1200 }),
  F('mango', 'Mango', 'produce', 0.8, 14, 0.4, { fiber: 1.6, unitWeightG: 250 }),
  F('arandanos', 'Arándanos', 'produce', 0.7, 12, 0.3, { fiber: 2.4, packG: 250 }),
  F('frambuesas', 'Frambuesas', 'produce', 1.2, 5, 0.7, { fiber: 6.5, packG: 250 }),
  F('datil', 'Dátiles', 'other', 2.5, 63, 0.4, { fiber: 8, unitWeightG: 8, packG: 250 }),
  F('pasas', 'Pasas', 'other', 3, 65, 0.5, { fiber: 4, packG: 250 }),
  F('orejones', 'Orejones de albaricoque', 'other', 3.4, 53, 0.5, { fiber: 7, packG: 250 }),
  F('limon', 'Limón', 'produce', 1.1, 3, 0.3, { fiber: 2.8, unitWeightG: 100 }),
  F('pomelo', 'Pomelo', 'produce', 0.8, 9, 0.1, { fiber: 1.6, unitWeightG: 300 }),
  F('papaya', 'Papaya', 'produce', 0.5, 10, 0.3, { fiber: 1.7, packG: 800 }),
  F('ciruela-pasa', 'Ciruelas pasas', 'other', 2.2, 55, 0.4, { fiber: 7, packG: 250 }),

  // ------------------------------------------------- frutos secos y semillas
  F('almendra', 'Almendras crudas', 'other', 21, 5, 50, { fiber: 12, packG: 200 }),
  F('nuez', 'Nueces', 'other', 15, 4, 65, { fiber: 6.7, packG: 200 }),
  F('avellana', 'Avellanas', 'other', 15, 5, 61, { fiber: 10, packG: 200 }),
  F('anacardo', 'Anacardos', 'other', 18, 27, 44, { fiber: 3.3, packG: 200 }),
  F('pistacho', 'Pistachos', 'other', 20, 17, 45, { fiber: 10, packG: 200 }),
  F('cacahuete', 'Cacahuetes', 'other', 25, 9, 48, { fiber: 8.5, packG: 200 }),
  F('crema-cacahuete', 'Crema de cacahuete 100 %', 'other', 25, 8, 50, { fiber: 8, packG: 350 }),
  F('semillas-chia', 'Semillas de chía', 'other', 17, 4, 31, { fiber: 34, packG: 250 }),
  F('semillas-lino', 'Semillas de lino', 'other', 18, 2, 42, { fiber: 27, packG: 250 }),
  F('semillas-sesamo', 'Semillas de sésamo', 'other', 17, 3, 50, { fiber: 12, packG: 250 }),
  F('pipas-girasol', 'Pipas de girasol peladas', 'other', 21, 11, 51, { fiber: 9, packG: 250 }),
  F('pipas-calabaza', 'Pipas de calabaza', 'other', 24, 11, 45, { fiber: 6, packG: 250 }),

  // ------------------------------------------------------ grasas y despensa
  F('aceite-oliva-virgen-extra', 'Aceite de oliva virgen extra', 'other', 0, 0, 100, { unitBase: 'ml', packG: 1000 }),
  F('aceite-girasol', 'Aceite de girasol', 'other', 0, 0, 100, { unitBase: 'ml', packG: 1000 }),
  F('vinagre-vino', 'Vinagre de vino', 'other', 0, 0.5, 0, { unitBase: 'ml', packG: 750 }),
  F('vinagre-modena', 'Vinagre de Módena', 'other', 0.5, 17, 0, { unitBase: 'ml', packG: 250 }),
  F('sal', 'Sal', 'other', 0, 0, 0, { packG: 1000 }),
  F('pimienta-negra', 'Pimienta negra molida', 'other', 10, 39, 3, { fiber: 25, packG: 50 }),
  F('pimenton-dulce', 'Pimentón dulce', 'other', 14, 34, 13, { fiber: 21, packG: 75 }),
  F('oregano', 'Orégano seco', 'other', 9, 22, 4, { fiber: 42, packG: 25 }),
  F('comino', 'Comino molido', 'other', 18, 33, 22, { fiber: 11, packG: 50 }),
  F('curry', 'Curry en polvo', 'other', 14, 25, 14, { fiber: 33, packG: 50 }),
  F('canela', 'Canela molida', 'other', 4, 27, 1.2, { fiber: 53, packG: 50 }),
  F('laurel', 'Hoja de laurel', 'other', 8, 27, 8, { fiber: 26, packG: 25 }),
  F('caldo-pollo', 'Caldo de pollo', 'other', 1.5, 1, 0.5, { unitBase: 'ml', packG: 1000, cookedNote: 'Valores del producto ya elaborado' }),
  F('caldo-verduras', 'Caldo de verduras', 'other', 0.8, 1.5, 0.3, { unitBase: 'ml', packG: 1000, cookedNote: 'Valores del producto ya elaborado' }),
  F('mostaza', 'Mostaza', 'other', 4.5, 4, 4, { fiber: 2, packG: 250 }),
  F('salsa-soja', 'Salsa de soja', 'other', 8, 5, 0.1, { unitBase: 'ml', packG: 250 }),
  F('miel', 'Miel', 'other', 0.4, 80, 0, { packG: 500, isTreat: true }),
  F('azucar', 'Azúcar', 'other', 0, 100, 0, { packG: 1000, isTreat: true }),
  F('cacao-puro', 'Cacao puro desgrasado', 'other', 20, 12, 11, { fiber: 30, packG: 250 }),
  F('chocolate-negro-85', 'Chocolate negro 85 %', 'other', 10, 15, 46, { fiber: 12, unitWeightG: 10, packG: 100, isTreat: true }),
  F('levadura-nutricional', 'Levadura nutricional', 'other', 45, 15, 5, { fiber: 22, packG: 125 }),
  F('gelatina-neutra', 'Gelatina neutra en polvo', 'other', 85, 0, 0, { packG: 100 }),
  F('tahini', 'Tahini', 'other', 17, 10, 54, { fiber: 9, packG: 300 }),
  F('concentrado-tomate', 'Concentrado de tomate', 'canned', 4.8, 14, 0.5, { fiber: 3, packG: 200 }),
  F('vinagre-manzana', 'Vinagre de manzana', 'other', 0, 0.9, 0, { unitBase: 'ml', packG: 500 }),

  // ---------------------------------------------------------- congelados
  F('guisantes-congelados', 'Guisantes congelados', 'frozen', 5.5, 9, 0.5, { fiber: 5, proteinSource: 'legume', packG: 750 }),
  F('menestra-congelada', 'Menestra de verduras congelada', 'frozen', 2.5, 6, 0.4, { fiber: 3, packG: 750 }),
  F('espinaca-congelada', 'Espinacas congeladas', 'frozen', 3, 1.5, 0.4, { fiber: 2.5, packG: 750 }),
  F('brocoli-congelado', 'Brócoli congelado', 'frozen', 3, 2.5, 0.4, { fiber: 3, packG: 750 }),
  F('judias-verdes-congeladas', 'Judías verdes congeladas', 'frozen', 1.8, 4, 0.2, { fiber: 3, packG: 750 }),
  F('frutos-rojos-congelados', 'Frutos rojos congelados', 'frozen', 1, 8, 0.4, { fiber: 4, packG: 500 }),
  F('merluza-congelada', 'Lomos de merluza congelados', 'frozen', 17, 0, 1.8, { proteinSource: 'fish', packG: 500 }),
  F('salmon-congelado', 'Lomos de salmón congelados', 'frozen', 20, 0, 13, { proteinSource: 'fish', packG: 500 }),
  F('gambas-congeladas', 'Gambas peladas congeladas', 'frozen', 19, 0, 1, { proteinSource: 'shellfish', packG: 400 }),
  F('patatas-congeladas-horno', 'Patatas gajo para horno', 'frozen', 2.5, 22, 4, { fiber: 2.5, packG: 750 }),
  F('coliflor-congelada', 'Coliflor congelada', 'frozen', 2, 3, 0.3, { fiber: 2.5, packG: 750 }),
  F('verduras-wok-congeladas', 'Verduras para wok congeladas', 'frozen', 1.8, 5, 0.3, { fiber: 2.5, packG: 750 }),
  F('edamame-congelado', 'Edamame congelado', 'frozen', 11, 3, 5, { fiber: 5, proteinSource: 'legume', packG: 500 }),
];

const POR_SLUG = new Map(FOODS.map((food) => [food.slug, food]));

export function findFood(slug: string): Food | undefined {
  return POR_SLUG.get(slug);
}

/** Como `findFood`, pero falla en vez de devolver `undefined`. */
export function requireFood(slug: string): Food {
  const food = POR_SLUG.get(slug);
  if (!food) throw new Error(`Alimento desconocido: ${slug}`);
  return food;
}

export function foodsBySection(section: SupermarketSection): Food[] {
  return FOODS.filter((food) => food.section === section);
}
